import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, Download, Loader2, Plus, Save } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { useLang } from "../i18n";
import { CheckGroup, Field, YesNo } from "../components/fields";
import { AttachmentInput, PublicLinkButton } from "../components/FormLink";
import CountrySelect from "../components/CountrySelect";
import SignaturePad from "../components/SignaturePad";
import FormRecordsList from "../components/FormRecordsList";
import DateInput from "../components/DateInput";

const ADDR_KEYS = ["country", "province", "district", "corregimiento", "urbanizacion", "street", "building", "apt"];

const initial = {
  completed_by: [],
  last_name_p: "", last_name_m: "", first_name: "", second_name: "",
  birth_date: "", birth_country: "", nationality: "", residence_country: "", gender: "",
  id_number: "", passport: "",
  addr_country: "", addr_province: "", addr_district: "", addr_corregimiento: "",
  addr_urbanizacion: "", addr_street: "", addr_building: "", addr_apt: "",
  mobile: "", home_phone: "", email: "", tax_places: "", nit: "",
  is_pep: null, pep_position: "",
  is_pep_relative: null, pep_rel_name: "", pep_rel_position: "", pep_rel_relation: "",
  is_pep_collab: null, pep_col_name: "", pep_col_position: "", pep_col_relation: "",
  annual_income: "", other_income_desc: "", other_income_amount: "",
  profession: "", occupation: "", employer: "",
  waddr_country: "", waddr_province: "", waddr_district: "", waddr_corregimiento: "",
  waddr_urbanizacion: "", waddr_street: "", waddr_building: "", waddr_apt: "",
  independent_activity: "",
  client_name: "", date: new Date().toISOString().slice(0, 10), signature: null,
  broker_name: "", broker_date: "", broker_signature: null,
  reviewer_name: "", reviewer_date: "", reviewer_signature: null,
};

export default function FormAseguradora({ publicToken = null }) {
  const { t } = useLang();
  const locationEdit = useLocation().state?.edit || null;
  const [editDoc, setEditDoc] = useState(locationEdit);
  const [showForm, setShowForm] = useState(!!locationEdit);
  const [d, setD] = useState(() => (locationEdit ? { ...initial, ...locationEdit.data } : initial));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [sec, setSec] = useState(0);
  const [atts, setAtts] = useState([]);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setD({ ...d, [k]: e?.target ? e.target.value : e });
  const inp = (key, testid, type = "text") => (
    type === "date"
      ? <DateInput value={d[key]} onChange={set(key)} data-testid={testid} />
      : <input className="ec-input" type={type} value={d[key]} onChange={set(key)} data-testid={testid} />
  );

  const scrollToForm = () => setTimeout(() => document.querySelector('[data-testid="as-form-wrap"]')?.scrollIntoView({ behavior: "smooth" }), 80);

  const newForm = () => {
    setEditDoc(null); setD(initial); setSaved(null); setSec(0); setShowForm(true); scrollToForm();
  };
  const startEdit = (doc) => {
    setEditDoc(doc); setD({ ...initial, ...doc.data }); setSaved(null); setSec(0); setShowForm(true); scrollToForm();
  };

  const AddrBlock = ({ pfx, testid }) => (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid={testid}>
      {ADDR_KEYS.map((k) => (
        <Field key={k} label={t(`a.addr_${k}`)}>
          <input className="ec-input" value={d[`${pfx}${k}`]} onChange={set(`${pfx}${k}`)} data-testid={`as-${pfx}${k}`} />
        </Field>
      ))}
    </div>
  );

  const PepBlock = ({ flag, fields, testid }) => (
    <div className="space-y-3" data-testid={testid}>
      <Field label={t(`a.${flag}`)}><YesNo value={d[flag]} onChange={(v) => setD({ ...d, [flag]: v })} testid={`as-${flag}`} /></Field>
      {d[flag] && (
        <div className="grid sm:grid-cols-3 gap-4">
          {fields.map(([k, lbl]) => (
            <Field key={k} label={t(lbl)}>{inp(k, `as-${k}`)}</Field>
          ))}
        </div>
      )}
    </div>
  );

  const SigCard = ({ title, nameKey, nameLabel, sigKey, dateKey, testid }) => (
    <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid={testid}>
      <h3 className="font-bold text-sm text-slate-800">{title}</h3>
      <Field label={nameLabel}><input className="ec-input" value={d[nameKey]} onChange={set(nameKey)} data-testid={`${testid}-name`} /></Field>
      <Field label={t("common.date")}><DateInput className="ec-input" value={d[dateKey]} onChange={set(dateKey)} data-testid={`${testid}-date`} /></Field>
      <Field label={t("forms.signature")}><SignaturePad onChange={(v) => setD((p) => ({ ...p, [sigKey]: v }))} /></Field>
    </div>
  );

  const sections = [
    {
      title: t("a.sec0"), testid: "sec-completer",
      content: (
        <Field label={t("a.completed_by")}>
          <CheckGroup
            options={[t("a.cb_contractor"), t("a.cb_insured"), t("a.cb_broker"), t("a.cb_payer"), t("a.cb_insurer"), t("a.cb_beneficiary")]}
            values={d.completed_by}
            onChange={(v) => setD({ ...d, completed_by: v })}
            testidPrefix="as-cb"
          />
        </Field>
      ),
    },
    {
      title: t("a.sec1"), testid: "sec-general",
      content: (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Field label={t("a.last_name_p")}>{inp("last_name_p", "as-last-name-p")}</Field>
            <Field label={t("a.last_name_m")}>{inp("last_name_m", "as-last-name-m")}</Field>
            <Field label={t("a.first_name")}>{inp("first_name", "as-first-name")}</Field>
            <Field label={t("a.second_name")}>{inp("second_name", "as-second-name")}</Field>
            <Field label={t("a.birth_date")}>{inp("birth_date", "as-birth-date", "date")}</Field>
            <Field label={t("a.birth_country")}><CountrySelect value={d.birth_country} onChange={(e) => setD((p) => ({ ...p, birth_country: e.target.value }))} testid="as-birth-country" /></Field>
            <Field label={t("a.nationality")}><CountrySelect value={d.nationality} onChange={(e) => setD((p) => ({ ...p, nationality: e.target.value }))} testid="as-nationality" /></Field>
            <Field label={t("a.residence_country")}><CountrySelect value={d.residence_country} onChange={(e) => setD((p) => ({ ...p, residence_country: e.target.value }))} testid="as-residence-country" /></Field>
            <Field label={t("a.gender")}>
              <select className="ec-input" value={d.gender} onChange={set("gender")} data-testid="as-gender">
                <option value="">—</option><option value="F">F</option><option value="M">M</option>
              </select>
            </Field>
            <Field label={t("a.id_number")}>{inp("id_number", "as-id-number")}</Field>
            <Field label={t("a.passport")}>{inp("passport", "as-passport")}</Field>
            <Field label={t("a.mobile")}>{inp("mobile", "as-mobile")}</Field>
            <Field label={t("a.home_phone")}>{inp("home_phone", "as-home-phone")}</Field>
            <Field label={t("a.email")}>{inp("email", "as-email", "email")}</Field>
            <Field label={t("a.tax_places")}>{inp("tax_places", "as-tax-places")}</Field>
            <Field label={t("a.nit")}>{inp("nit", "as-nit")}</Field>
          </div>
          <div>
            <h3 className="font-bold text-sm text-slate-800 mb-3">{t("a.addr_res")}</h3>
            <AddrBlock pfx="addr_" testid="as-addr-res" />
          </div>
        </div>
      ),
    },
    {
      title: t("a.sec2"), testid: "sec-pep",
      content: (
        <div className="space-y-6">
          <PepBlock flag="is_pep" fields={[["pep_position", "a.pep_position"]]} testid="as-pep-self" />
          <PepBlock flag="is_pep_relative" fields={[["pep_rel_name", "a.pep_name"], ["pep_rel_position", "a.pep_rel_position"], ["pep_rel_relation", "a.pep_relation"]]} testid="as-pep-relative" />
          <PepBlock flag="is_pep_collab" fields={[["pep_col_name", "a.pep_name"], ["pep_col_position", "a.pep_rel_position"], ["pep_col_relation", "a.pep_relation"]]} testid="as-pep-collab" />
        </div>
      ),
    },
    {
      title: t("a.sec3"), testid: "sec-financial",
      content: (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("a.annual_income")}>
            <select className="ec-input" value={d.annual_income} onChange={set("annual_income")} data-testid="as-annual-income">
              <option value="">—</option>
              {[t("a.r1"), t("a.r2"), t("a.r3"), t("a.r4")].map((r) => <option key={r}>{r}</option>)}
            </select>
          </Field>
          <Field label={t("a.other_income_desc")}>{inp("other_income_desc", "as-other-income-desc")}</Field>
          <Field label={t("a.other_income_amount")}>{inp("other_income_amount", "as-other-income-amount")}</Field>
        </div>
      ),
    },
    {
      title: t("a.sec4"), testid: "sec-work",
      content: (
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label={t("a.profession")}>{inp("profession", "as-profession")}</Field>
            <Field label={t("a.occupation")}>{inp("occupation", "as-occupation")}</Field>
            <Field label={t("a.employer")}>{inp("employer", "as-employer")}</Field>
          </div>
          <div>
            <h3 className="font-bold text-sm text-slate-800 mb-3">{t("a.addr_work")}</h3>
            <AddrBlock pfx="waddr_" testid="as-addr-work" />
          </div>
          <Field label={t("a.independent")}>
            <textarea className="ec-input" rows={2} value={d.independent_activity} onChange={set("independent_activity")} data-testid="as-independent" />
          </Field>
        </div>
      ),
    },
    {
      title: t("a.sec5"), testid: "sec-consent",
      content: (
        <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 leading-relaxed" data-testid="as-consent-text">
          {t("a.consent")}
        </p>
      ),
    },
    {
      title: t("a.sec6"), testid: "sec-signatures",
      content: (
        <div className="grid md:grid-cols-3 gap-5">
          <SigCard title={t("a.sig_client")} nameKey="client_name" nameLabel={t("a.sig_name")} sigKey="signature" dateKey="date" testid="as-sig-client" />
          <SigCard title={t("a.sig_broker")} nameKey="broker_name" nameLabel={t("a.sig_name")} sigKey="broker_signature" dateKey="broker_date" testid="as-sig-broker" />
          <SigCard title={t("a.sig_insurer")} nameKey="reviewer_name" nameLabel={t("a.sig_reviewer")} sigKey="reviewer_signature" dateKey="reviewer_date" testid="as-sig-insurer" />
        </div>
      ),
    },
    {
      title: t("a.sec7"), testid: "sec-docs",
      content: (
        <div className="space-y-4">
          <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="as-docs-text">{t("a.docs")}</p>
          <AttachmentInput value={atts} onChange={setAtts} testid="as-attachments" />
        </div>
      ),
    },
  ];

  const submit = async () => {
    if (!d.signature) {
      toast.error(t("forms.sign_required"));
      setSec(6);
      return;
    }
    setBusy(true);
    try {
      if (publicToken) {
        const { data } = await api.post(`/public/form/${publicToken}`, { data: d, attachments: atts });
        setDone(data.form_number);
      } else if (editDoc) {
        await api.patch(`/forms/${editDoc.id}`, { data: d });
        setSaved({ id: editDoc.id, form_number: editDoc.form_number });
        toast.success(t("forms.updated"));
      } else {
        const { data } = await api.post("/forms", { form_type: "dd_pn_aseguradora", data: d, attachments: atts });
        setSaved(data);
        toast.success(`${t("forms.save_success")} — ${data.form_number}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="ec-card p-10 text-center space-y-3 max-w-xl mx-auto" data-testid="form-public-done">
        <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
        <h2 className="font-heading text-xl font-extrabold text-slate-900">{t("forms.public_done")}</h2>
        <p className="text-sm text-slate-600">{t("forms.public_done_no")} <span className="font-mono font-bold">{done}</span></p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="form-aseguradora-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("a.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("a.subtitle")}</p>
        </div>
        {!publicToken && (
          <div className="flex items-center gap-2">
            <button onClick={newForm} data-testid="as-create-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
              <Plus className="w-4 h-4" /> {t("frl.new")}
            </button>
            <PublicLinkButton formType="dd_pn_aseguradora" testid="as-public-link" />
          </div>
        )}
      </div>

      {!publicToken && <FormRecordsList formType="dd_pn_aseguradora" onEdit={startEdit} refresh={saved} testid="as" />}

      {saved && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="form-saved-banner">
          <span className="text-sm text-emerald-800 font-semibold">{t("forms.doc_no")} <span className="font-mono">{saved.form_number}</span></span>
          <button onClick={() => downloadFile(`/forms/${saved.id}/pdf`, `${saved.form_number}.pdf`)} data-testid="download-form-pdf" className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#033A8C]">
            <Download className="w-3.5 h-3.5" /> {t("common.download_pdf")}
          </button>
          <button onClick={() => { setD(initial); setEditDoc(null); setSaved(null); setSec(0); }} data-testid="new-form-button" className="text-xs font-bold text-slate-600 hover:text-slate-900 underline">
            {t("n.rt_new")}
          </button>
        </div>
      )}

      {(publicToken || showForm) && (
        <div className="space-y-6" data-testid="as-form-wrap">
          <div className="space-y-1.5" data-testid="as-progress-wrap">
            <div className="flex justify-between text-[11px] font-semibold text-[#8A94A2]">
              <span>{sections[sec].title}</span>
              <span data-testid="as-progress-pct">{Math.round(((sec + 1) / sections.length) * 100)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#EFF4FC] overflow-hidden">
              <div className="h-full rounded-full bg-[#044CBC] transition-all duration-500" style={{ width: `${((sec + 1) / sections.length) * 100}%` }} data-testid="as-progress" />
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto pb-1">
            {sections.map((s, i) => (
              <button key={i} onClick={() => setSec(i)} data-testid={`as-tab-${i}`} className={`shrink-0 px-3.5 py-2 rounded-lg text-xs font-bold transition-colors ${sec === i ? "bg-[#16335F] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                {s.title}
              </button>
            ))}
          </div>

          <div className="ec-card p-6" data-testid={`as-section-${sections[sec].testid}`}>
            <h2 className="font-heading font-bold text-slate-900 mb-5">{sections[sec].title}</h2>
            {sections[sec].content}
            <div className="flex justify-between mt-8">
              <button onClick={() => setSec(Math.max(0, sec - 1))} disabled={sec === 0} data-testid="as-prev" className="px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-30">←</button>
              {sec < sections.length - 1 ? (
                <button onClick={() => setSec(sec + 1)} data-testid="as-next" className="px-6 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C]">→</button>
              ) : (
                <button onClick={submit} disabled={busy} data-testid="as-submit" className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {busy ? t("common.saving") : t("common.save")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
